import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  Sparkles, TrendingUp, TrendingDown, Minus, Calendar, Brain, BarChart3
} from "lucide-react";
import { ShimmerPanel } from "@/components/ui/Shimmer";

const PredictionPanel = ({ prediction, onOpenForecast }) => {
  const { t } = useTranslation();
  const [displayData, setDisplayData] = useState(null);
  const hasLoadedOnce = useRef(false);

  // Update display data only when we have new valid data
  useEffect(() => {
    if (prediction?.prediction) {
      setDisplayData(prediction);
      hasLoadedOnce.current = true;
    }
  }, [prediction]);
  
  // Show shimmer only on first load
  if (!displayData?.prediction) {
    if (!hasLoadedOnce.current) {
      return <ShimmerPanel type="prediction" title={t('aiPrediction') || 'AI Prediction'} />;
    }
    return (
      <div className="prediction-panel-enhanced" data-testid="prediction-panel">
        <div className="pred-header">
          <div className="pred-title">
            <Sparkles size={14} />
            <span>{t('aiPrediction') || 'AI Prediction'}</span>
          </div>
        </div>
        <div className="empty-state">{t('analyzing') || 'Analyzing...'}</div>
      </div>
    );
  }

  const pred = displayData.prediction;
  const confidence = Math.round(displayData.confidence * 100);
  const Icon = pred.direction === 'up' ? TrendingUp : pred.direction === 'down' ? TrendingDown : Minus;
  const directionColor = pred.direction === 'up' ? '#22c55e' : pred.direction === 'down' ? '#ef4444' : '#6b7280';

  return (
    <div className="prediction-panel-enhanced" data-testid="prediction-panel">
      {/* Header */}
      <div className="pred-header">
        <div className="pred-title">
          <Sparkles size={14} />
          <span>{t('aiPrediction') || 'AI Prediction'}</span>
        </div>
        <div className="pred-confidence" style={{ color: directionColor }}>
          {confidence}% confidence
        </div>
      </div>

      {/* Main Prediction */}
      <div className="pred-main">
        <motion.h3 
          className="pred-mood"
          style={{ color: pred.color }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          key={pred.mood}
        >
          {pred.mood?.toUpperCase()}
        </motion.h3>
        
        <div className="pred-meta">
          <div 
            className="pred-direction"
            style={{ backgroundColor: `${directionColor}15`, color: directionColor }}
          >
            <Icon size={14} />
            <span>{pred.direction?.toUpperCase()}</span>
          </div>
          {pred.pantone && (
            <div className="pred-pantone">
              <div className="pred-color-dot" style={{ backgroundColor: pred.color }} />
              <span>{pred.pantone.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Synopsis Section */}
      <div className="pred-synopsis">
        <div className="synopsis-header">
          <Brain size={12} />
          <span>Analysis Synopsis</span>
        </div>
        <p className="synopsis-text">
          {pred.reasoning || "Based on analysis of recent global news sentiment, social media trends, and historical mood patterns."}
        </p>
        <div className="synopsis-meta">
          <span><BarChart3 size={10} /> {displayData.based_on_samples || 24} data points</span>
          <span>
            {displayData.intensity_trend > 0.01 ? <TrendingUp size={10} /> : 
             displayData.intensity_trend < -0.01 ? <TrendingDown size={10} /> : 
             <Minus size={10} />}
            {displayData.intensity_trend > 0 ? '+' : ''}{Math.round((displayData.intensity_trend || 0) * 100)}% trend
          </span>
        </div>
      </div>

      {/* Key Factors */}
      {displayData.key_factors && displayData.key_factors.length > 0 && (
        <div className="pred-factors">
          <span className="factors-label">Key Factors</span>
          <div className="factors-list">
            {displayData.key_factors.slice(0, 4).map((f, i) => (
              <span key={i} className="factor-tag">{f}</span>
            ))}
          </div>
        </div>
      )}

      {/* 7-Day Forecast Button - Opens Modal */}
      <button 
        className="pred-forecast-btn"
        onClick={onOpenForecast}
        data-testid="open-forecast-modal-btn"
      >
        <Calendar size={14} />
        <span>7-Day Forecast</span>
      </button>
    </div>
  );
};

export { PredictionPanel };
